import {
  getEffectOpenQuestionsRequest,
  getGoodTeamSurveyResultsRequest,
  IGetOpenQuestionsResp,
  StructuredAnalysisData,
} from './../services/reports';
import { createContext, useCallback, useContext, useState } from 'react';
import { useFilters } from './useFilters';
import { ITeamComparisonItem, getTeamsComparisonRequest } from '../services/groupComparison';
import {
  IGetRecommendationsRequest,
  IGetSurveyResultsResponse,
  ISuplementaryResourceItem,
  getRecommendationsRequest,
  getResourcesRequest,
  getSurveyResultsRequest,
  IGetReportDetailsResp,
  getEffectSurveyResultsRequest,
  IGetEffectReportDetailsResp,
} from '../services/reports';
import { IPagingInfo } from '../services/surveys';
import { EReportType } from '../constans/report';
import { surveyTypes } from '../constans/forms';

interface IReportDetailsStateContext {
  data: IReportData | null;
  meta: IReportMeta | null;
  isLoading: boolean;
  details: IGetReportDetailsResp;
  roles: string[];
  effectData: IGetEffectReportDetailsResp | null;
  structuredEffectData: StructuredAnalysisData;
  openQuestions: IGetOpenQuestionsResp | null;
}

interface IReportDetailsHandlersContext {
  getReportGroupComparisonDetails: (id: string) => Promise<void>;
  getReportResourcesDetails: (id: string) => Promise<void>;
  getReportDetails: (id: string) => Promise<void>;
}

export const ReportDetailsStateContext = createContext<IReportDetailsStateContext | undefined>(
  undefined
);

export const ReportDetailsHandlersContext = createContext<
  IReportDetailsHandlersContext | undefined
>(undefined);

export const useReportContext = () => {
  const state = useContext(ReportDetailsStateContext) as IReportDetailsStateContext;
  const handlers = useContext(ReportDetailsHandlersContext) as IReportDetailsHandlersContext;

  if (!state && !handlers) {
    throw new Error('useReportContext must be used within a ReportDetailsProvider');
  }

  return {
    ...state,
    ...handlers,
  };
};

export interface IReportData {
  results?: IGetSurveyResultsResponse;
  groupComparison?: ITeamComparisonItem[];
  recommendations?: IGetRecommendationsRequest;
  resources?: ISuplementaryResourceItem[];
}

export interface IReportEffectData {
  results?: IGetEffectReportDetailsResp;
}

export interface IReportMeta {
  groupComparison?: IPagingInfo;
  resources?: IPagingInfo;
}

export type TogglerKeys =
  | 'historical_result'
  | 'agreement'
  | 'variation_between_respondents'
  | 'organization_mean'
  | 'external_benchmark'
  | 'team_manager';

interface IUseReport {
  reportType: EReportType;
  surveyType: string;
  isOpenComments?: boolean;
}

export const useReport = ({ reportType, surveyType, isOpenComments }: IUseReport) => {
  const { currentFilters } = useFilters();

  const [data, setData] = useState<IReportData | null>(null);
  const [effectData, setEffectData] = useState<IGetEffectReportDetailsResp | null>(null);
  const [openQuestions, setOpenQuestions] = useState<IGetOpenQuestionsResp | null>(null);
  const [error, setError] = useState<{} | null>(null);
  const [meta, setMeta] = useState<IReportMeta | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { compare, compareSort, ln, pageNo, perPage } = currentFilters ?? {};

  const getReportResults = useCallback(
    async (id: string) =>
      await getSurveyResultsRequest(id, { ln: typeof ln === 'string' ? ln : 'en' }),
    [ln]
  );

  const getReportGroupComparison = useCallback(
    async (id: string) => {
      const filters =
        compare && typeof compare === 'string'
          ? (JSON.parse(compare) as { risk: string[]; team: string[] })
          : null;
      const sort =
        compareSort && typeof compareSort === 'string'
          ? (JSON.parse(compareSort) as { [key: string]: 'asc' | 'desc' })
          : null;

      const [key, value] = sort ? Object.entries(sort).flat(1) : [];

      return await getTeamsComparisonRequest(id, {
        pageNo: pageNo && typeof pageNo === 'string' ? pageNo : '1',
        perPage: perPage && typeof perPage === 'string' ? perPage : '10',
        risk: filters?.risk,
        sortOrder: sort ? `${key}:${value}` : undefined,
        teamId: filters?.team,
      });
    },
    [compare, compareSort, pageNo, perPage]
  );

  const getReportRecommendations = useCallback(
    async (id: string) => await getRecommendationsRequest(id),
    []
  );

  const getReportResources = useCallback(
    async (id: string) =>
      await getResourcesRequest(id, {
        pageNo: pageNo && typeof pageNo === 'string' ? pageNo : '1',
        perPage: perPage && typeof perPage === 'string' ? perPage : '10',
      }),
    [pageNo, perPage]
  );

  const getReportDetails = useCallback(
    async (id: string) => {
      setIsLoading(true);

      try {
        if (surveyType === surveyTypes.EFFECT) {
          const [effectResult, openQuestions] = await Promise.allSettled([
            getEffectSurveyResultsRequest(id),
            getEffectOpenQuestionsRequest(id),
          ]);
          if (effectResult.status === 'fulfilled') {
            setEffectData(effectResult.value);
          }

          if (openQuestions.status === 'fulfilled' && isOpenComments) {
            setOpenQuestions(openQuestions.value);
          }
        } else if (surveyType === surveyTypes.GOOD_TEAM) {
          const effectResult = await Promise.allSettled([getGoodTeamSurveyResultsRequest(id)]);
          if (effectResult[0].status === 'fulfilled') {
            setEffectData(effectResult[0].value);
          }
        } else {
          const [results, recommendations, resources, groupComparison] = await Promise.allSettled([
            getReportResults(id),
            getReportRecommendations(id),
            getReportResources(id),
            reportType === EReportType.executive ? getReportGroupComparison(id) : null,
          ]);

          if (results.status === 'fulfilled') {
            setData(state => ({ ...state, results: results.value }));
          }

          if (recommendations.status === 'fulfilled') {
            setData(state => ({ ...state, recommendations: recommendations.value }));
          }

          if (resources.status === 'fulfilled') {
            setData(state => ({ ...state, resources: resources.value.items }));
            setMeta(meta => ({ ...meta, resources: resources.value.pagingInfo }));
          }

          if (groupComparison && groupComparison.status === 'fulfilled') {
            setData(state => ({ ...state, groupComparison: groupComparison?.value?.items }));
            setMeta(meta => ({ ...meta, groupComparison: groupComparison?.value?.pagingInfo }));
          }
        }
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      surveyType,
      isOpenComments,
      reportType,
      getReportResults,
      getReportGroupComparison,
      getReportRecommendations,
      getReportResources,
    ]
  );

  const getReportGroupComparisonDetails = useCallback(
    async (id: string) => {
      setIsLoading(true);

      try {
        const response = await getReportGroupComparison(id);

        setData(state => ({ ...state, groupComparison: response.items }));
        setMeta(meta => ({ ...meta, groupComparison: response.pagingInfo }));
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [getReportGroupComparison]
  );

  const getReportResourcesDetails = useCallback(
    async (id: string) => {
      setIsLoading(true);

      try {
        const response = await getReportResources(id);

        setData(state => ({ ...state, resources: response.items }));
        setMeta(meta => ({ ...meta, resources: response.pagingInfo }));
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    },
    [getReportResources]
  );

  return {
    data,
    error,
    meta,
    getReportDetails,
    getReportGroupComparisonDetails,
    getReportResourcesDetails,
    isLoading,
    effectData,
    openQuestions,
  };
};
